import { useCallback, useEffect, useRef, useState } from 'react';
import { AxiosError } from 'axios';
import { listExpected } from '../../api/visitor-passes-client';
import { ExpectedResponse, ExpectedRow, PassAccess } from '../../interfaces/visitor-pass';
import { usePortalLiveRefresh } from './use-portal-live-refresh';

/**
 * TODAY'S EXPECTED VISITORS FOR ONE BUILDING — GET /buildings/:b/expected.
 *
 * One reader for the two desk surfaces that need it (resident-portal Plan 2,
 * RP-2.11): the main menu's Expected card (its count, and whether it shows at
 * all) and the Expected today screen.
 *
 * ── WHEN IT RE-READS ──
 * A desk tablet sits on this screen for hours, and "who is expected now"
 * changes with the clock rather than with anything the tablet does. So:
 *   - on window focus and when the tab becomes visible again;
 *   - every 60 s while the page is visible (a hidden tab costs nothing);
 *   - when the bell's socket delivers a `visitor_pass.*` or `visitor.arrived`
 *     frame (usePortalLiveRefresh listens to the ONE socket the app holds, it
 *     never opens a second);
 *   - whenever the caller asks (after every sign-in and sign-out).
 *
 * ── ONE READ WINS, AND IT IS THE LATEST ONE ──
 * Several reads are routinely in the air at once (a focus, a poll and a
 * sign-in's refresh). Without the sequence number the LAST RESPONSE wins rather
 * than the LATEST REQUEST, and an older list overtaking a newer one puts
 * somebody just signed in back on the list as expected. The same guard the
 * exit desk carries (visitor-exit-page.tsx readSeq).
 *
 * A changed building throws the old answer away at once: a count from the
 * previous building must never sit on the card for the new one.
 */

export const EXPECTED_POLL_MS = 60 * 1000;

/** The bell frames that mean this list may have changed. Prefixes. */
export const EXPECTED_LIVE_EVENTS = ['visitor_pass.', 'visitor.arrived'];

export type ExpectedLoadError = 'denied' | 'failed';

export interface ExpectedToday {
  /** null until the first read for this building answers. */
  rows: ExpectedRow[] | null;
  /** The building's live switch, null until known. false hides the card. */
  passesEnabled: boolean | null;
  /** What this caller may add at the building (migration 118), null until known. */
  passAccess: PassAccess | null;
  deskDay: ExpectedResponse['desk_day'] | null;
  /** The server's clock at the moment it answered. */
  generatedAt: string | null;
  /** The first read is in flight and nothing has answered yet. */
  loading: boolean;
  /** A later read is in flight over an answer already on screen. */
  refreshing: boolean;
  /** 'denied' = 403; 'failed' = anything else. Cleared by the next good read. */
  error: ExpectedLoadError | null;
  refresh: () => Promise<void>;
}

export interface UseExpectedTodayOptions {
  /** Off = no reads at all (no permission, or the building is switched off). */
  enabled?: boolean;
}

export const useExpectedToday = (
  buildingId: number | null | undefined,
  { enabled = true }: UseExpectedTodayOptions = {}
): ExpectedToday => {
  const id =
    typeof buildingId === 'number' && Number.isInteger(buildingId) && buildingId > 0
      ? buildingId
      : null;
  const active = enabled && id !== null;

  const [data, setData] = useState<ExpectedResponse | null>(null);
  const [inFlight, setInFlight] = useState(false);
  const [error, setError] = useState<ExpectedLoadError | null>(null);
  const readSeq = useRef(0);

  const refresh = useCallback(async (): Promise<void> => {
    if (!active || id === null) return;
    const seq = ++readSeq.current;
    setInFlight(true);
    try {
      const res = await listExpected(id);
      if (seq !== readSeq.current) return;
      setData(res);
      setError(null);
    } catch (e) {
      if (seq !== readSeq.current) return;
      const status = (e as AxiosError)?.response?.status;
      setError(status === 403 ? 'denied' : 'failed');
    } finally {
      if (seq === readSeq.current) setInFlight(false);
    }
  }, [active, id]);

  // A new building (or the gate opening or closing) starts from nothing. The
  // bump invalidates any read still in flight for the previous building.
  useEffect(() => {
    readSeq.current += 1;
    setData(null);
    setError(null);
    setInFlight(false);
    if (active) void refresh();
  }, [active, id, refresh]);

  useEffect(() => {
    if (!active) return undefined;

    const onFocus = () => {
      void refresh();
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') void refresh();
    };
    const timer = window.setInterval(() => {
      if (document.visibilityState !== 'hidden') void refresh();
    }, EXPECTED_POLL_MS);

    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      window.clearInterval(timer);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [active, refresh]);

  usePortalLiveRefresh(EXPECTED_LIVE_EVENTS, () => {
    if (active) void refresh();
  });

  return {
    rows: data ? data.rows : null,
    passesEnabled: data ? data.passes_enabled : null,
    passAccess: data?.pass_access ?? null,
    deskDay: data ? data.desk_day : null,
    generatedAt: data ? data.generated_at : null,
    loading: active && data === null && inFlight,
    refreshing: data !== null && inFlight,
    error,
    refresh,
  };
};
